import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wifi, Lock, Cog, ShieldCheck } from "lucide-react";

/**
 * SETTINGS simulation. Used by password practices and secure devices/networks.
 * Events: password_set, weak_password_set, mfa_enabled, network_selected,
 * public_network_used, device_locked, update_installed, final_decision.
 */
export default function SettingsSim({ scenario, recorder, onFinalize, busy }) {
  const [pw, setPw] = useState("");
  const [pwSet, setPwSet] = useState(false);
  const [mfa, setMfa] = useState(false);
  const [network, setNetwork] = useState(null);
  const [locked, setLocked] = useState(false);
  const [updated, setUpdated] = useState(false);

  const minLen = scenario.min_password_len || 12;
  const weak = (scenario.weak_examples || []).map((s) => s.toLowerCase());
  const networks = scenario.networks || [];

  const setPassword = () => {
    const ok = pw.length >= minLen && !weak.includes(pw.toLowerCase());
    setPwSet(true);
    recorder.log("password_set", pw.length + "chars");
    if (!ok) recorder.log("weak_password_set", pw);
  };
  const enableMfa = () => { setMfa(true); recorder.log("mfa_enabled"); };
  const pickNetwork = (n) => {
    setNetwork(n);
    recorder.log("network_selected", n.ssid);
    if (!n.secure) recorder.log("public_network_used", n.ssid);
  };
  const lockDevice = () => { setLocked(true); recorder.log("device_locked"); };
  const installUpdate = () => { setUpdated(true); recorder.log("update_installed"); };

  const finalize = () => {
    onFinalize("secured", { pw_len: pw.length, mfa, network, locked, updated });
  };

  return (
    <div className="grid md:grid-cols-2 gap-0 min-h-[440px]" data-testid="settings-sim">
      <div className="border-b md:border-b-0 md:border-r border-[var(--iddl-rule)] p-5">
        <div className="label-mono flex items-center gap-2"><Lock size={12} /> Authentication</div>
        <div className="mt-2 grid gap-2">
          <Input
            type="password"
            placeholder={`At least ${minLen} characters`}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)] h-10"
            data-testid="input-password"
          />
          <div className="flex gap-2">
            <Button size="sm" className="btn-ink rounded-none" onClick={setPassword} data-testid="btn-set-password">Set password</Button>
            <Button size="sm" variant="outline" className={`rounded-none ${mfa ? "bg-[var(--iddl-surface)]" : ""}`} onClick={enableMfa} data-testid="btn-enable-mfa">
              <ShieldCheck size={12} className="mr-1" /> {mfa ? "MFA enabled" : "Enable MFA"}
            </Button>
          </div>
          {pwSet && <div className="label-mono mt-2">{pw.length}-char password set {weak.includes(pw.toLowerCase()) || pw.length < minLen ? "(weak)" : "(strong)"}</div>}
        </div>
      </div>

      <div className="p-5">
        <div className="label-mono flex items-center gap-2"><Wifi size={12} /> Network &amp; device</div>
        {networks.length > 0 && (
          <ul className="mt-2 grid gap-2">
            {networks.map((n) => (
              <li key={n.ssid}>
                <button
                  onClick={() => pickNetwork(n)}
                  data-testid={`net-${n.ssid}`}
                  className={`w-full text-left p-3 border ${network?.ssid === n.ssid ? "border-[var(--iddl-ink)] bg-[var(--iddl-surface)]" : "border-[var(--iddl-rule)] hover:bg-[var(--iddl-surface)]"}`}
                >
                  <div className="font-mono-disp text-sm">{n.ssid}</div>
                  <div className="label-mono mt-1">{n.secure ? "ENCRYPTED" : "OPEN — RISK"}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="rounded-none" onClick={lockDevice} data-testid="btn-lock-device">
            <Lock size={12} className="mr-1" /> {locked ? "Auto-lock on" : "Enable auto-lock"}
          </Button>
          <Button size="sm" variant="outline" className="rounded-none" onClick={installUpdate} data-testid="btn-install-update">
            <Cog size={12} className="mr-1" /> {updated ? "Up-to-date" : "Install pending update"}
          </Button>
        </div>
      </div>

      <div className="md:col-span-2 p-4 border-t border-[var(--iddl-rule)] flex justify-end">
        <Button onClick={finalize} disabled={busy} className="btn-ink rounded-none" data-testid="btn-finalize-settings">Finalise — submit setup</Button>
      </div>
    </div>
  );
}
